import { Link, useLocation, useRouteError, isRouteErrorResponse } from "react-router-dom";
import { Button } from "../../components/ui/Button";
import { Alert } from "../../components/ui/Alert";

/* -------------------------------------------------------------------------- */
/*  Shared shell                                                              */
/* -------------------------------------------------------------------------- */

function ErrorShell({
  code,
  title,
  description,
  primaryAction,
  secondaryAction,
  diagnostics,
}: {
  code: string;
  title: React.ReactNode;
  description: React.ReactNode;
  primaryAction?: React.ReactNode;
  secondaryAction?: React.ReactNode;
  diagnostics?: React.ReactNode;
}) {
  return (
    <main
      id="main-content"
      role="main"
      className="flex min-h-screen items-center justify-center bg-[var(--c-surface-muted)] px-4 py-12"
    >
      <div className="max-w-xl text-center">
        <div className="text-5xl font-bold text-[var(--c-brand-strong)]">{code}</div>
        <h1 className="mt-3 text-xl font-semibold text-default">{title}</h1>
        <p className="mt-2 text-sm text-muted">{description}</p>
        <div className="mt-6 flex items-center justify-center gap-2">
          {primaryAction}
          {secondaryAction}
        </div>
        {diagnostics && <div className="mt-6 text-left">{diagnostics}</div>}
      </div>
    </main>
  );
}

/* -------------------------------------------------------------------------- */
/*  404                                                                       */
/* -------------------------------------------------------------------------- */

export function NotFoundPage() {
  const location = useLocation();
  return (
    <ErrorShell
      code="404"
      title={<>Page not found · पृष्ठ सापडले नाही</>}
      description={
        <>The page <code className="font-mono text-xs">{location.pathname}</code> doesn&apos;t exist or has been moved.</>
      }
      primaryAction={
        <Link to="/dashboard"><Button variant="primary">Back to dashboard</Button></Link>
      }
      secondaryAction={
        <Button variant="ghost" onClick={() => window.history.back()}>Go back</Button>
      }
    />
  );
}

/* -------------------------------------------------------------------------- */
/*  403                                                                       */
/* -------------------------------------------------------------------------- */

export function ForbiddenPage() {
  return (
    <ErrorShell
      code="403"
      title={<>Not authorised · प्रवेश नाकारला</>}
      description="Your account doesn't have permission to view this page. Contact your supervisor if you believe this is in error."
      primaryAction={<Link to="/dashboard"><Button variant="primary">Dashboard</Button></Link>}
      secondaryAction={<Link to="/help"><Button variant="ghost">Help &amp; support</Button></Link>}
    />
  );
}

/* -------------------------------------------------------------------------- */
/*  500 (Router error boundary)                                               */
/* -------------------------------------------------------------------------- */

export function RouterErrorPage() {
  const err = useRouteError();
  const status = isRouteErrorResponse(err) ? err.status : 500;
  const detail =
    isRouteErrorResponse(err)
      ? err.statusText
      : (err as Error)?.message || "Unknown error";

  return (
    <ErrorShell
      code={String(status)}
      title={<>Something went wrong · त्रुटी आली</>}
      description="The application hit an unexpected problem. The dev team has been notified."
      primaryAction={
        <Button variant="primary" onClick={() => window.location.reload()}>Reload page</Button>
      }
      secondaryAction={
        <Link to="/dashboard"><Button variant="ghost">Dashboard</Button></Link>
      }
      diagnostics={
        <Alert tone="neutral" title="Diagnostics">
          <pre className="mt-1 max-h-40 overflow-auto whitespace-pre-wrap break-words text-[11px] text-muted">
            {detail}
          </pre>
        </Alert>
      }
    />
  );
}

/* -------------------------------------------------------------------------- */
/*  Generic server-down                                                       */
/* -------------------------------------------------------------------------- */

export function ServerDownPage() {
  return (
    <ErrorShell
      code="503"
      title={<>Server unavailable · सेवा अनुपलब्ध</>}
      description="The backend is temporarily unreachable. Your queued writes will sync automatically once the connection is restored."
      primaryAction={<Button variant="primary" onClick={() => window.location.reload()}>Retry</Button>}
    />
  );
}
